/**
 * Parse weekly Keyfuels diesel price lines from plain text (no PDF / OCR).
 *
 * Expected line shape:
 *   "Your Keyfuels Diesel fuel price for week commencing 20/07/26 is 137.50 pence per litre + VAT"
 */

import { isValidCalendarYmd } from './calendarYmd';

export type KeyfuelsDieselPriceParse = {
  weekCommencingYmd: string;
  pencePerLitre: number;
  matchedLine: string;
  matchCount: number;
};

/** Case-insensitive; tolerates extra whitespace. Captures DD/MM/YY(YY) and rate. */
export const KEYFUELS_DIESEL_PRICE_RE =
  /Your\s+Keyfuels\s+Diesel\s+fuel\s+price\s+for\s+week\s+commencing\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\s+is\s+(\d+(?:\.\d+)?)\s+pence\s+per\s+litre(?:\s*\+\s*VAT)?/gi;

/** Convert DD/MM/YY or DD/MM/YYYY → YYYY-MM-DD (2-digit years → 2000+). */
export function ukDateToYmd(raw: string): string | null {
  const m = String(raw).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  let y = Number(m[3]);
  if (y < 100) y += 2000;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const ymd = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return isValidCalendarYmd(ymd) ? ymd : null;
}

/**
 * Parse Keyfuels diesel weekly price line(s) from plain text.
 * Uses the first match; `matchCount` > 1 means the source had multiple price lines.
 */
export function parseKeyfuelsDieselPriceLine(text: string): KeyfuelsDieselPriceParse | null {
  const re = new RegExp(KEYFUELS_DIESEL_PRICE_RE.source, 'gi');
  const matches = [...text.matchAll(re)];
  if (matches.length === 0) return null;

  const first = matches[0];
  const weekRaw = first[1];
  const rateRaw = first[2];
  const weekCommencingYmd = ukDateToYmd(weekRaw);
  const pencePerLitre = Number(rateRaw);
  if (!weekCommencingYmd || !Number.isFinite(pencePerLitre)) return null;

  return {
    weekCommencingYmd,
    pencePerLitre,
    matchedLine: first[0].replace(/\s+/g, ' ').trim(),
    matchCount: matches.length,
  };
}
